import { useEffect, useState } from 'react';
import { Avatar } from '../../components/Avatar.tsx';
import { Icon } from '../../components/Icon.tsx';
import { ErrorState } from '../../components/DataState.tsx';
import { ApiError } from '../../api/client.ts';
import { addComment, loadReactions, removeComment, toggleEmoji } from '../../api/share.ts';
import { COMMENT_MAX_LENGTH, EMOJIS, emojiLabel } from '../../domain.ts';
import type { Reactions as Data } from '../../domain.ts';

/**
 * 이모티콘 · 코멘트 — 기획서 3.5.
 *
 * **기도 노트에서만** 씁니다. 모임 기록지에는 없습니다 — 기록지는 그 자리에
 * 모인 사람들이 함께 적는 것이라, 거기에 다시 반응을 달면 누가 무엇을 말한
 * 것인지가 흐려집니다.
 *
 * 이모티콘은 **한 사람에 하나**입니다. 다른 것을 누르면 갈아타고, 같은 것을
 * 다시 누르면 뗍니다 — 「어떻게 반응했나」이지 「몇 번 눌렀나」가 아닙니다.
 * 켜고 끄는 판단은 **서버가** 합니다. 화면이 스스로 세면 같은 순간 남이 누른
 * 것과 어긋납니다.
 *
 * 다섯 개가 **언제나 같은 자리**에 있습니다. 개수 순으로 세우면 누를 때마다
 * 자리가 바뀌어 두 번째를 못 누릅니다.
 */

interface Props {
  noteId: string;
}

export function Reactions({ noteId }: Props) {
  const [data, setData] = useState<Data | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    loadReactions(noteId)
      .then((d) => alive && setData(d))
      .catch((e: unknown) => {
        if (alive) setFailure(e instanceof ApiError ? e.message : '반응을 불러오지 못했습니다.');
      });
    return () => {
      alive = false;
    };
  }, [noteId]);

  /** 바뀐 전체를 받아 갈아 끼웁니다 — 화면이 따로 셈하지 않습니다 */
  const run = async (work: () => Promise<Data>) => {
    setBusy(true);
    setFailure(null);
    try {
      setData(await work());
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : '남기지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  if (data === null) {
    return failure ? <ErrorState message={failure} onRetry={() => setFailure(null)} /> : null;
  }

  const countOf = (emoji: string) => data.emojis.find((e) => e.emoji === emoji);

  return (
    <section className="react">
      <div className="react__bar" role="group" aria-label="이모티콘">
        {EMOJIS.map((emoji) => {
          const hit = countOf(emoji);
          return (
            <button
              key={emoji}
              type="button"
              className={hit?.mine ? 'react__chip react__chip--on' : 'react__chip'}
              aria-pressed={hit?.mine ?? false}
              aria-label={emojiLabel[emoji]}
              title={emojiLabel[emoji]}
              disabled={busy}
              onClick={() => void run(() => toggleEmoji(noteId, emoji))}
            >
              <span className="react__emoji">{emoji}</span>
              {/* 아무도 안 누른 것에는 0 을 적지 않습니다 — 빈 자리가 덜 시끄럽습니다 */}
              {hit && <span className="react__count">{hit.count}</span>}
            </button>
          );
        })}
      </div>

      <CommentList
        comments={data.comments}
        busy={busy}
        onRemove={(id) => void run(() => removeComment(id))}
      />

      <CommentBox busy={busy} onSend={(body) => void run(() => addComment(noteId, body))} />

      {failure && <p className="failure">{failure}</p>}
    </section>
  );
}

function CommentList({
  comments,
  busy,
  onRemove,
}: {
  comments: Data['comments'];
  busy: boolean;
  onRemove: (reactionId: string) => void;
}) {
  if (comments.length === 0) {
    return <p className="react__empty">아직 코멘트가 없습니다.</p>;
  }

  return (
    <ul className="react__list">
      {comments.map((c) => (
        <li key={c.reactionId} className="react__item">
          <Avatar name={c.authorName ?? '?'} seed={c.authorId} />
          <div className="react__body">
            <p className="react__who">{c.authorName ?? '이름 없음'}</p>
            <p className="react__text">{c.body}</p>
          </div>
          {/* 남긴 사람과 노트 주인만 — 누가 지울 수 있는지는 서버가 정합니다 */}
          {c.canRemove && (
            <button
              type="button"
              className="react__remove"
              aria-label="코멘트 지우기"
              disabled={busy}
              onClick={() => onRemove(c.reactionId)}
            >
              <Icon name="close" size={14} />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function CommentBox({
  busy,
  onSend,
}: {
  busy: boolean;
  onSend: (body: string) => void;
}) {
  const [body, setBody] = useState('');
  const left = COMMENT_MAX_LENGTH - body.length;

  const send = () => {
    const text = body.trim();
    if (text === '') return;
    onSend(text);
    setBody('');
  };

  return (
    <div className="react__write">
      <input
        className="input"
        value={body}
        placeholder="코멘트 남기기"
        maxLength={COMMENT_MAX_LENGTH}
        aria-label="코멘트"
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') send();
        }}
      />
      {/* 상한이 가까울 때만 셈을 보여 줍니다 — 늘 떠 있으면 재촉처럼 읽힙니다 */}
      {left <= 20 && <span className="react__left">{left}</span>}
      <button
        type="button"
        className="btn btn--primary react__send"
        disabled={busy || body.trim() === ''}
        onClick={send}
      >
        남기기
      </button>
    </div>
  );
}
